import React from "react";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {DropDownModel} from "../../../models/dropDown";
import Error from "../error/error";
import {observer} from "mobx-react";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {MenuItem} from "@mui/material";
import ReloadButton from "../reloadButton/reloadButton";
import i18n from "../../../../i18n/i18n";

interface MultiSelectDropDownProps {
    onChange?: (newValue: any) => void;
    validatedValue: ValidatedValue<string[]>;
    items: DropDownModel[];
    label: string;
    testId: string;
    height?: number;
    className?: string;
    disabled?: boolean | false;
    withEmptyValue?: boolean | false;
    onClose?: () => void;
    withoutSort?: boolean;
    error?: string;
    reloadFunc?: () => void;
    isNoRequired?: boolean;
}

function MultiSelectDropDown(props:MultiSelectDropDownProps) {
    const labelId = `label-${props.testId}`;

    return (
        <div className="dropdown-form">
            <FormControl
                className={`dropdown ${props.className || ""} ${displayError() || displayErrorWhenDataNotLoaded() ? "has-error" : ""} ${displayErrorWhenDataNotLoaded() && props.isNoRequired ? "no-required" : ""}`}
                data-testid={props.testId}>
                <InputLabel disabled={props.disabled} id={labelId}>{props.label}</InputLabel>
                <Select
                    value={props.validatedValue.value}
                    onChange={onChange}
                    error={displayError()}
                    onBlur={validate}
                    onClose={() => {
                        if (props.onClose) {
                            props.onClose();
                        }
                        validate();
                    }}
                    className="select"
                    data-testid={`${props.testId}-select`}
                    MenuProps={{PaperProps: {sx: {maxHeight: props.height || 300}}}}
                    disabled={props.disabled}
                    labelId={labelId}
                    label={props.label}
                    multiple
                >
                    {getItems().map((item) => (
                        <MenuItem value={item.id} key={item.id} data-testid={`${props.testId}-${item.id}`}>
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
                {displayError() && (
                    <Error message={props.validatedValue.errorMessage}/>
                )}
                {displayErrorWhenDataNotLoaded() && (
                    <Error message={i18n.t("defaultNoDataLoadErrorMessage", {messageSuffix: props.error})}/>
                )}
            </FormControl>
            {displayErrorWhenDataNotLoaded() && props.reloadFunc && (
                <ReloadButton
                    className={props.isNoRequired ? "no-required" : ""}
                    reloadFunc={props.reloadFunc}
                    testId={`${props.testId}-reload`}
                />
            )}
        </div>
    );

    function onChange(event: SelectChangeEvent<string[]>) {
        const value = event.target.value;

        props.validatedValue.clearValidation();

        props.validatedValue.setValue(typeof value === "string" ? value.split(",") : value);

        if (props.onChange) {
            props.onChange(value);
        }
    }

    function validate() {
        props.validatedValue.validate();
    }

    function displayError(): boolean {
        return !!props.validatedValue.errorMessage;
    }

    function displayErrorWhenDataNotLoaded(): boolean {
        return !!props.error;
    }

    function getItems(): DropDownModel[] {
        const items = getSortItems(props.items);

        if (!props.withEmptyValue) {
            return items;
        }

        const emptyElement: DropDownModel[] = [{id: "", name: ""}];
        return emptyElement.concat(items);
    }

    function getSortItems(items: DropDownModel[]): DropDownModel[] {
        if (props.withoutSort) {
            return items;
        }

        return items.sort((a,b) => {
            if (a && b && typeof a.name === "string" && typeof b.name === "string") {
                return a.name.localeCompare(b.name);
            } else return 1;
        });
    }
}

export default observer(MultiSelectDropDown);
